import React from 'react';
import {
  Box,
  Container,
  Typography,
  Chip,
  Card,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Button,
} from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { Link as RouterLink } from 'react-router-dom';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import ContactFormDialog from '../components/ContactFormDialog';

const EndUserPrivacyPage: React.FC = () => {
  const [contactOpen, setContactOpen] = React.useState(false);
  return (
    <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
      <Navbar />

      <Box sx={{ pt: 15, pb: 10 }}>
        <Container maxWidth="md">
          <Box sx={{ textAlign: 'center', mb: 6 }}>
            <Chip label="Legal" color="secondary" sx={{ mb: 2 }} />
            <Typography variant="h2" fontWeight="bold" gutterBottom>
              End-User Privacy Policy
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
              Last updated: February 17, 2026
            </Typography>
            <Typography variant="body2" color="text.secondary">
              This policy applies to individuals who interact with businesses using CleoLead's platform.
            </Typography>
            <Button
              component={RouterLink}
              to="/privacy"
              variant="text"
              size="small"
              sx={{ mt: 1, color: '#C75B39' }}
            >
              Looking for the CleoLead User Privacy Policy?
            </Button>
          </Box>

          <Card sx={{ p: 4, mb: 4 }}>
            <Typography variant="body1" paragraph>
              This End-User Privacy Policy explains how CleoLead Corp. ("CleoLead", "we", "our", or "us")
              collects, uses, and protects your personal information when you communicate with a business
              that uses CleoLead's AI-assisted customer engagement platform. If you are messaging a business
              on Facebook, Instagram, Telegram, or another supported channel and that business uses CleoLead,
              this policy applies to you.
            </Typography>
          </Card>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            1. Who We Are
          </Typography>
          <Typography variant="body1" paragraph>
            CleoLead Corp. provides AI-assisted tools that help businesses respond to customer messages.
            When you message a business that uses CleoLead, your conversation may be processed by our
            platform to generate responses, capture inquiries, and help the business serve you better.
          </Typography>
          <Typography variant="body1" paragraph>
            The business you are communicating with is the <strong>data controller</strong> for your
            personal information. CleoLead acts as a <strong>data processor</strong> on behalf of that business.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            2. Information We Collect
          </Typography>
          <Typography variant="body1" paragraph>
            When you interact with a business using CleoLead, the following information may be collected:
          </Typography>
          <List>
            {[
              'Your name and profile information as provided by the messaging platform',
              'The content of your messages and conversations',
              'Timestamps and metadata of your interactions',
              'Contact information you voluntarily provide (e.g., phone number, email)',
              'Your messaging platform user ID',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            3. How Your Information Is Used
          </Typography>
          <Typography variant="body1" paragraph>
            Your information is used to:
          </Typography>
          <List>
            {[
              'Generate AI-assisted responses to your messages on behalf of the business',
              'Help the business manage and respond to your inquiries',
              'Capture lead information so the business can follow up with you',
              'Improve the quality and relevance of AI-generated responses',
              'Provide analytics to the business about their customer interactions',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            4. AI Processing and Profiling
          </Typography>
          <Typography variant="body1" paragraph>
            Our AI may analyze your conversation content to provide better service. This may include:
          </Typography>
          <List>
            {[
              'Understanding the topic or intent of your message',
              'Identifying your preferences and interests based on conversation context',
              'Assessing communication style to provide more relevant responses',
              'Extracting facts you mention (e.g., preferred appointment times, product interests)',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          <Typography variant="body1" paragraph>
            This profiling is used solely to help the business serve you better and is not used
            for advertising or sold to third parties. You may request deletion of your profile
            data at any time.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            5. Third-Party AI Service Providers
          </Typography>
          <Typography variant="body1" paragraph>
            To generate responses and process conversations, your message data may be sent to
            third-party AI service providers, including:
          </Typography>
          <List>
            {[
              'Anthropic (Claude) — primary LLM for generating conversation responses and analyzing conversations',
              'OpenAI — image/vision analysis and knowledge-base embeddings',
              'Google (Gemini) — AI image generation for assistant avatars',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          <Typography variant="body1" paragraph>
            These providers process data according to their own privacy policies and data processing
            agreements. Conversation data sent to these providers is used solely for generating
            responses and is not used to train their models.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            6. Data Sharing
          </Typography>
          <Typography variant="body1" paragraph>
            Your conversation data is shared with:
          </Typography>
          <List>
            {[
              'The business you are communicating with (they have full access to your conversations)',
              'Third-party AI service providers (as described in Section 5, solely for response generation)',
              'Law enforcement or regulatory authorities, when required by law',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          <Typography variant="body1" paragraph>
            We do not sell your personal information to third parties.
          </Typography>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            7. Data Storage and Security
          </Typography>
          <Typography variant="body1" paragraph>
            Your data is stored securely with the following protections:
          </Typography>
          <List>
            {[
              'All data is encrypted at rest using AES-256 encryption',
              'Data in transit is protected with TLS 1.3 encryption',
              'Strict data isolation between different businesses on our platform',
              'Regular security assessments and monitoring',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            8. Data Retention
          </Typography>
          <List>
            {[
              'Conversation data: Retained while the business account is active, deleted within 90 days of account closure',
              'Your profile data: Retained while the associated business account is active',
              'Analytics data: Aggregated and anonymized after 12 months',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            9. Your Rights
          </Typography>
          <Typography variant="body1" paragraph>
            You have the right to:
          </Typography>
          <List>
            {[
              'Know what personal data we hold about you',
              'Request a copy of your data',
              'Request correction of inaccurate data',
              'Request deletion of your data',
              'Object to processing of your data',
              'Withdraw consent at any time',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            10. How to Request Data Deletion
          </Typography>
          <Typography variant="body1" paragraph>
            To request deletion of your personal data:
          </Typography>
          <List>
            {[
              'Contact support@cleolead.com with your request',
              'Specify the platform (Facebook, Instagram, Telegram) and your username or ID',
              'We will process your request within 30 days',
              'For Facebook/Instagram users: Meta may also send deletion requests on your behalf, which we process automatically via our data deletion callback',
              'You will receive confirmation once your data has been deleted',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>
          <Card sx={{ p: 3, bgcolor: 'rgba(59, 130, 246, 0.1)', mt: 3 }}>
            <Typography variant="body1">
              <strong>Note:</strong> You can also contact the business you were communicating with
              directly to request data deletion. As the data controller, the business is responsible
              for handling your request and instructing us to delete your data.
            </Typography>
          </Card>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            11. GDPR Compliance (European Economic Area)
          </Typography>
          <Typography variant="body1" paragraph>
            For end-users in the European Economic Area (EEA):
          </Typography>
          <List>
            {[
              'Data Controller: The business you are communicating with',
              'Data Processor: CleoLead Corp.',
              'Data Protection Contact: support@cleolead.com',
              'Lawful bases for processing: Consent (messaging initiation), Legitimate Interests (responding to your inquiry)',
              'Cross-border transfers: Data may be transferred to the US and Canada. We use standard contractual clauses to protect your data.',
              'Right to complain: You may lodge a complaint with your local data protection authority',
            ].map((item, index) => (
              <ListItem key={index} sx={{ py: 0.5 }}>
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <CheckCircle color="primary" fontSize="small" />
                </ListItemIcon>
                <ListItemText primary={item} />
              </ListItem>
            ))}
          </List>

          <Typography variant="h4" fontWeight="bold" gutterBottom sx={{ mt: 6 }}>
            12. Contact Us
          </Typography>
          <Typography variant="body1" paragraph>
            If you have questions about this Privacy Policy or how your data is being used, please contact:
          </Typography>
          <Card sx={{ p: 3, bgcolor: 'background.paper' }}>
            <Typography variant="body1" paragraph>
              <strong>CleoLead Corp.</strong>
            </Typography>
            <Typography variant="body1">
              <strong>Email:</strong> <span onClick={() => setContactOpen(true)} style={{ color: '#C75B39', cursor: 'pointer', textDecoration: 'underline' }} role="button" tabIndex={0} onKeyDown={(e) => { if (e.key === 'Enter') setContactOpen(true); }}>support@cleolead.com</span>
            </Typography>
          </Card>
        </Container>
      </Box>

      <Footer />
      <ContactFormDialog
        open={contactOpen}
        onClose={() => setContactOpen(false)}
        title="Privacy Inquiry"
        subtitle="Have questions about end-user privacy? Send us a message."
        formType="privacy"
        defaultSubject="Privacy Inquiry - End User"
      />
    </Box>
  );
};

export default EndUserPrivacyPage;
